<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import GroupedSelect from "../../../../components/ui/form/GroupedSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import PhoneInput from "../../../../components/ui/form/PhoneInput.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import { TableAction, TableField } from "../../../../components/table/table";
import DataTable from "../../../../components/table/DataTable.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import PosStationsForm from "./PosStationsForm.vue";
import LabelLayout from "./LabelLayout.vue";
import MessageBox from "../../../../services/MessageBox";
import { copyText } from "../../../../utils/CommonFunctions";
import OutletTablesForm from "./OutletTablesForm.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  logo: props.data?.logo || "",
  id: props.data?.id || "",
  code: props.data?.code || "",
  name: props.data?.name || props.listCreateName || "",
  prefix: props.data?.prefix || "",
  suffix: props.data?.suffix || "",
  warehouses_id: props.data?.warehouses_id || "",
  sales_channels_id: props.data?.sales_channels_id || "",
  price_lists_id: props.data?.price_lists_id || "",
  address: props.data?.address || "",
  buildings_id: props.data?.buildings_id || "",
  inventory_account_id: props.data?.inventory_account_id || "",
  revenue_account_id: props.data?.revenue_account_id || "",
  currencys_id: props.data?.currencys_id || "",
  credit_terms_id: props.data?.credit_terms_id || "",
  tax_identification_no: props.data?.tax_identification_no || "",
  lat: props.data?.lat || "",
  lng: props.data?.lng || "",
  tel: props.data?.tel || "",
  mobile: props.data?.mobile || "",
  viber: props.data?.viber || "",
  whatsapp: props.data?.whatsapp || "",
  email: props.data?.email || "",
  url: props.data?.url || "",
  opening_hours: props.data?.opening_hours || "",
  letterhead: props.data?.letterhead || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
  pos_stations: props.data?.pos_stations || [],
  tables: props.data?.tables || [],
  price_label_layout: props.data?.price_label_layout || "",
  service_charge: props.data?.service_charge || 0,
  service_charge_accounts_id: props.data?.service_charge_accounts_id || "",
  service_charge_tax_details_id: props.data?.service_charge_tax_details_id || "",
  footer_text: props.data?.footer_text || "",
  max_discount: props.data?.max_discount || 0,
  type: props.data?.type || "",
});

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("Name is required")]);
setFieldRules("warehouses_id", [required("Warehouse is required")]);
setFieldRules("currencys_id", [required("Currency is required")]);
//setFieldRules("inventory_account_id", [required("Inventory Account is required")]);
setFieldRules("sales_channels_id", [required("Sales Channel is required")]);
setFieldRules("price_lists_id", [required("Price List is required")]);
setFieldRules("credit_terms_id", [required("Credit Term is required")]);
setFieldRules("mobile", [required("Mobile No is required")]);
setFieldRules("type", [required("Type is required")]);

const shopTypes = ref([
  { id: "Shop", name: "Shop" },
  { id: "Restaurant", name: "Restaurant" },
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (fields.value.service_charge.value > 0) {
    setFieldRules("service_charge_accounts_id", [required("Service Charge Account is required")]);
    setFieldRules("service_charge_tax_details_id", [required("Service Charge Tax is required")]);
  }
  else {
    setFieldRules("service_charge_accounts_id", []);
    setFieldRules("service_charge_tax_details_id", []);
  }
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        response.data;
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    setFormData(props.data);
  }
});

const posStationTableFields: TableField[] = [
  { name: "name", caption: "Name", sortable: true, fieldType: "text" },
  {
    name: "pos_printer",
    caption: "POS Printer",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "display_terminal",
    caption: "Display Terminal",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "balance_screen_secs",
    caption: "Balance Screen (Secs.)",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "touch_screen",
    caption: "Touch Screen",
    sortable: true,
    fieldType: "boolean",
  },
  {
    name: "receipt_type",
    caption: "Receipt Type",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "recepit_size",
    caption: "Receipt Size",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "device_name",
    caption: "Device Name",
    sortable: true,
    fieldType: "text",
  },
  { name: "active", caption: "active", sortable: false, fieldType: "status" },
];

const posStationActions: TableAction[] = [
  {
    name: "edit",
    icon: "Pen",
    variant: "primary",
    title: "Edit",
    permission: "create",
    visible: (item) => item.active == 1 || item.active == true,
  },
  {
    name: "otp",
    icon: "LockKeyholeOpen",
    variant: "primary",
    title: "Generate OTP",
    permission: "create",
    visible: (item) => item.active == 1 || item.active == true,
  },
];

const tableFields: TableField[] = [
  {
    name: "table_number",
    caption: "Table Number",
    sortable: true,
    fieldType: "text",
  },
  { name: "type", caption: "Type", sortable: true, fieldType: "text" },
  { name: "capacity", caption: "Capacity", sortable: true, fieldType: "text" },
  { name: "color", caption: "Color", sortable: true, fieldType: "text" },
  { name: "active", caption: "active", sortable: false, fieldType: "status" },
];

const showPosStationForm = ref(false);
const selectedPosStation = ref(null);
const posStationFormMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("create");

const showTableForm = ref(false);
const selectedTable = ref(null);
const tableFormMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("create");

const handlePermissionPosStationActions = (action: string, item: any) => {
  console.log("permissions", props.permissions);
  console.log(action, item);
  if (action == "otp") {
    handleGenerateOTP(item);
  } else if (action == "edit") {
    posStationFormMode.value = "edit";
    selectedPosStation.value = item;
    showPosStationForm.value = !showPosStationForm.value;
  } else if (action == "view") {
    posStationFormMode.value = "view";
    selectedPosStation.value = item;
    showPosStationForm.value = !showPosStationForm.value;
  }
};

declare global {
  interface Window {
    copyText: (memo_no: string) => void;
  }
}
window.copyText = copyText;

const handleGenerateOTP = async (data: any) => {
  if (props.data) {
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "otp-pos-station",
      {
        outlets_id: props.data.id,
        pos_stations_id: data.uid,
      }
    );
    if (response.success == true) {
      MessageBox.show(
        "OTP is <strong><u><span style='color: #F44336'>" +
          response.data +
          "</span></u></strong>" +
          '<button class="ml-2" onclick="window.copyText(\'' +
          response.data +
          "')\">" +
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-clipboard" viewBox="0 0 16 16">' +
          '<path d="M10 1.5v1h2a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1h2v-1a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5zM9 1H7v1h2V1z"/>' +
          '<path d="M3.5 3a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-11a.5.5 0 0 0-.5-.5h-9z"/>' +
          "</svg>" +
          "</button>" +
          " , OTP will expir in 10 minutes",
        "info"
      );
    }
  }
};

const handleNewPosStation = () => {
  posStationFormMode.value = "create";
  selectedPosStation.value = null;
  showPosStationForm.value = !showPosStationForm.value;
};

const handleNewTable = () => {
  tableFormMode.value = "create";
  selectedTable.value = null;
  showTableForm.value = !showTableForm.value;
};
const updateLabelLayout = (data: string) => {
  formData.value.price_label_layout = data;
};
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
    width="45rem"
  >
    <template #form>
      <div class="grid md:grid-cols-4 gap-6">
        <FileUpload
          accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
          label="Logo"
          :max-size="5"
          :multiple="false"
          :readonly="isReadOnly"
          :image="true"
          path="outlets"
          v-model="fields.logo.value"
        />
        <div class="grid md:grid-cols-4 gap-6 col-span-3">
          <SearchSelect
            v-model="fields.type.value"
            label="Type"
            class="col-span-2"
            :options="shopTypes"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="fields.type.touched ? fields.type.error : null"
            @blur="fields.type.touched = true"
          />

          <TextInput
            v-model="fields.code.value"
            label="Code"
            placeholder="Enter Code"
            :readonly="isReadOnly"
            :error="fields.code.touched ? fields.code.error : null"
            @blur="fields.code.touched = true"
          />

          <TextInput
            v-model="fields.name.value"
            class="col-span-3"
            label="Name"
            :readonly="isReadOnly"
            :error="fields.name.touched ? fields.name.error : null"
            @blur="fields.name.touched = true"
          />

          <SearchSelect
            v-model="fields.warehouses_id.value"
            label="Warehouse"
            class="col-span-2"
            end-point="system/company/warehouses"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="
              fields.warehouses_id.touched ? fields.warehouses_id.error : null
            "
            @blur="fields.warehouses_id.touched = true"
          />

          <SearchSelect
            v-model="fields.price_lists_id.value"
            label="Price List"
            class="col-span-2"
            end-point="finance/master-data/price-lists"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="
              fields.price_lists_id.touched ? fields.price_lists_id.error : null
            "
            @blur="fields.price_lists_id.touched = true"
          />
        </div>
      </div>
      <div class="grid md:grid-cols-3 gap-6">
        <div class="grid md:grid-cols-2 gap-6 col-span-2">
          <SearchSelect
            v-model="fields.sales_channels_id.value"
            end-point="revenue/master-data/sales-channels"
            label="Sales Channel"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="
              fields.sales_channels_id.touched
                ? fields.sales_channels_id.error
                : null
            "
            @blur="fields.sales_channels_id.touched = true"
          />

          <SearchSelect
            v-model="fields.currencys_id.value"
            end-point="finance/master-data/currency"
            label="Currency"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="
              fields.currencys_id.touched ? fields.currencys_id.error : null
            "
            @blur="fields.currencys_id.touched = true"
          />
        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <TextInput
            v-model="fields.prefix.value"
            label="Prefix"
            :readonly="isReadOnly"
            :error="fields.prefix.touched ? fields.prefix.error : null"
            @blur="fields.prefix.touched = true"
          />

          <TextInput
            v-model="fields.suffix.value"
            label="Suffix"
            :readonly="isReadOnly"
            :error="fields.suffix.touched ? fields.suffix.error : null"
            @blur="fields.suffix.touched = true"
          />
        </div>
      </div>
      <div class="grid md:grid-cols-3 gap-6">
        <SearchSelect
          v-model="fields.credit_terms_id.value"
          end-point="finance/master-data/credit-terms"
          label="Payment Term"
          :allow-new-record="true"
          :readonly="isReadOnly"
          :error="
            fields.credit_terms_id.touched ? fields.credit_terms_id.error : null
          "
          @blur="fields.credit_terms_id.touched = true"
        />

        <TextInput
          v-model="fields.tax_identification_no.value"
          label="Tax Identification No"
          :readonly="isReadOnly"
          :error="
            fields.tax_identification_no.touched
              ? fields.tax_identification_no.error
              : null
          "
          @blur="fields.tax_identification_no.touched = true"
        />

        <SearchSelect
          v-model="fields.revenue_account_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Revenue Account"
          :allow-new-record="true"
          :readonly="isReadOnly"
          :error="
            fields.revenue_account_id.touched
              ? fields.revenue_account_id.error
              : null
          "
          @blur="fields.revenue_account_id.touched = true"
        />

        <TextInput
          v-model="fields.address.value"
          label="Address"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.address.touched ? fields.address.error : null"
          @blur="fields.address.touched = true"
        />

        <GroupedSelect
          v-model="fields.buildings_id.value"
          label="Building"
          end-point="system/master-data/buildings"
          :allow-new-record="true"
          optionLabel="buildings"
          :readonly="isReadOnly"
          :error="
            fields.buildings_id.touched ? fields.buildings_id.error : null
          "
          @blur="fields.buildings_id.touched = true"
        />

        <NumberInput
          v-model="fields.lat.value"
          label="Latitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lat.touched ? fields.lat.error : null"
          @blur="fields.lat.touched = true"
        />

        <NumberInput
          v-model="fields.lng.value"
          label="Longitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lng.touched ? fields.lng.error : null"
          @blur="fields.lng.touched = true"
        />

        <PhoneInput
          v-model="fields.tel.value"
          label="Phone"
          :readonly="isReadOnly"
          :error="fields.tel.touched ? fields.tel.error : null"
          @blur="fields.tel.touched = true"
        />

        <PhoneInput
          v-model="fields.mobile.value"
          label="Mobile"
          :readonly="isReadOnly"
          :error="fields.mobile.touched ? fields.mobile.error : null"
          @blur="fields.mobile.touched = true"
        />

        <PhoneInput
          v-model="fields.viber.value"
          label="Viber"
          :readonly="isReadOnly"
          :error="fields.viber.touched ? fields.viber.error : null"
          @blur="fields.viber.touched = true"
        />

        <PhoneInput
          v-model="fields.whatsapp.value"
          label="WhatsApp"
          :readonly="isReadOnly"
          :error="fields.whatsapp.touched ? fields.whatsapp.error : null"
          @blur="fields.whatsapp.touched = true"
        />

        <TextInput
          v-model="fields.email.value"
          label="Email"
          :readonly="isReadOnly"
          :error="fields.email.touched ? fields.email.error : null"
          @blur="fields.email.touched = true"
        />

        <TextInput
          v-model="fields.url.value"
          label="URL"
          :readonly="isReadOnly"
          :error="fields.url.touched ? fields.url.error : null"
          @blur="fields.url.touched = true"
        />

        <TextInput
          v-model="fields.opening_hours.value"
          label="Opening Hours"
          :readonly="isReadOnly"
          :error="
            fields.opening_hours.touched ? fields.opening_hours.error : null
          "
          @blur="fields.opening_hours.touched = true"
        />

        <NumberInput
          v-model="fields.max_discount.value"
          label="Maximum Discount (%)"
          :readonly="isReadOnly"
          :min="0"
          :error="
            fields.max_discount.touched ? fields.max_discount.error : null
          "
          @blur="fields.max_discount.touched = true"
        />
        </div>
        <div class="grid md:grid-cols-3 gap-6">

        <NumberInput
          v-model="fields.service_charge.value"
          label="Service Charge (%)"
          :readonly="isReadOnly"
          :min="0"
          :error="
            fields.service_charge.touched ? fields.service_charge.error : null
          "
          @blur="fields.service_charge.touched = true"
        />

        <SearchSelect
            v-model="fields.service_charge_accounts_id.value"
            v-if="fields.service_charge.value > 0"
            label="Service Charge Account"
            end-point="finance/accounting/chart-of-accounts"
            extra1="Income"
            :allow-new-record="false"
            :readonly="isReadOnly"
            :error="
              fields.service_charge_accounts_id.touched ? fields.service_charge_accounts_id.error : null
            "
            @blur="fields.service_charge_accounts_id.touched = true"
          />

          <SearchSelect
            v-model="fields.service_charge_tax_details_id.value"
            v-if="fields.service_charge.value > 0"
            label="Service Charge Tax"
            end-point="finance/master-data/tax-definitions"
            :allow-new-record="false"
            :readonly="isReadOnly"
            :error="
              fields.service_charge_tax_details_id.touched ? fields.service_charge_tax_details_id.error : null
            "
            @blur="fields.service_charge_tax_details_id.touched = true"
          />

        <TextAreaInput
          v-model="fields.footer_text.value"
          label="Footer Text"
          class="col-span-3"
          :readonly="isReadOnly"
          :error="fields.footer_text.touched ? fields.footer_text.error : null"
          @blur="fields.footer_text.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
      <hr />
      <div v-if="mode != 'create'">
        <div class="mt-6">
          <div class="flex justify-between border-b-2 border-gray-200 pb-4">
            <h3>POS Stations</h3>
            <Buttons
              caption="Add POS Station"
              icon="Plus"
              @click="handleNewPosStation"
            />
          </div>
          <DataTable
            :rows="fields.pos_stations.value"
            :fields="posStationTableFields"
            :actions="posStationActions"
            :permissions="permissions"
            title="Tables"
            :remote="false"
            @action="handlePermissionPosStationActions"
            :show-header="false"
            :allow-disable="true"
          />
        </div>
        <div v-if="fields.type.value == 'Restaurant'" class="mt-6">
          <div class="flex justify-between border-b-2 border-gray-200 pb-4">
            <h3>Tables</h3>
            <Buttons caption="Add Table" icon="Plus" @click="handleNewTable" />
          </div>
          <DataTable
            :rows="fields.tables.value"
            :fields="tableFields"
            :permissions="permissions"
            title="Tables"
            :remote="false"
            @action="handlePermissionPosStationActions"
            :show-header="false"
            :allow-disable="true"
          />
        </div>
      </div>
      <!--<div v-if="mode != 'create'">-->
      <PosStationsForm
        v-if="showPosStationForm"
        :data="selectedPosStation"
        @close="showPosStationForm = false"
        :end-point="endPoint"
        :permissions="permissions"
        :mode="posStationFormMode"
        :outlet="formData"
        @save="setFormData"
      />

      <OutletTablesForm
        v-if="showTableForm"
        :data="selectedTable"
        @close="showTableForm = false"
        :end-point="endPoint"
        :permissions="permissions"
        :mode="tableFormMode"
        :outlet="formData"
        @save="setFormData"
      />
      <!--</div>-->

      <div>
        <LabelLayout
          :layout="formData.price_label_layout"
          :readonly="isReadOnly"
          @save="updateLabelLayout"
          class="mt-4"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
